import React, { useEffect, useState } from 'react';
import { Loader2, Database, Shield, BarChart3, CheckCircle2 } from 'lucide-react';

const SystemLoadingScreen = ({ status }) => {
    const { overall_progress = 0, current_stage = "Initializing...", estimated_time_remaining = 0, overall_ready = false } = status || {};
    const [isVisible, setIsVisible] = useState(true);

    // Smooth fade-out when system is ready
    useEffect(() => {
        if (overall_ready) {
            // Wait a bit before starting fade out
            const timer = setTimeout(() => {
                setIsVisible(false);
            }, 800);
            return () => clearTimeout(timer);
        }
    }, [overall_ready]);

    return (
        <div
            className={`fixed inset-0 z-50 transition-opacity duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
            style={{
                background: 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #475569 100%)',
            }}
        >
            {/* Animated Background Elements */}
            <div className="absolute inset-0 overflow-hidden">
                {/* Subtle Grid Pattern */}
                <div className="absolute inset-0" style={{
                    backgroundImage: 'radial-gradient(circle, rgba(148, 163, 184, 0.1) 1px, transparent 1px)',
                    backgroundSize: '50px 50px',
                    opacity: 0.5
                }} />
            </div>

            {/* Main Content */}
            <div className="relative h-full flex items-center justify-center p-4">
                <div
                    className="relative bg-white/10 backdrop-blur-2xl rounded-3xl shadow-2xl border border-white/20 p-10 max-w-lg w-full"
                    style={{
                        animation: 'fadeInScale 0.8s ease-out',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 100px rgba(255, 255, 255, 0.1)'
                    }}
                >
                    {/* Logo/Icon Section */}
                    <div className="text-center mb-8">
                        <div className="relative inline-block mb-6">
                            {/* Rotating Ring */}
                            <div className="absolute inset-0 rounded-full border-4 border-white/30 animate-spin-slow" style={{ animationDuration: '3s' }} />

                            {/* Main Icon Container */}
                            <div className={`relative inline-flex items-center justify-center w-24 h-24 rounded-full transition-all duration-700 ${overall_ready
                                ? 'bg-gradient-to-br from-emerald-500 to-green-600 shadow-lg shadow-green-500/30'
                                : 'bg-gradient-to-br from-blue-500 to-blue-700 shadow-lg shadow-blue-500/30'
                                }`}>
                                {overall_ready ? (
                                    <CheckCircle2 className="w-12 h-12 text-white animate-scale-in" strokeWidth={2.5} />
                                ) : (
                                    <Loader2 className="w-12 h-12 text-white animate-spin" strokeWidth={2.5} />
                                )}

                                {/* Pulsing Ring Effect */}
                                {!overall_ready && (
                                    <div className="absolute inset-0 rounded-full bg-blue-400/30 animate-ping" style={{ animationDuration: '2s' }} />
                                )}
                            </div>
                        </div>

                        <h1 className="text-3xl font-bold text-white mb-3 drop-shadow-lg" style={{ animation: 'fadeInUp 0.6s ease-out 0.2s both' }}>
                            {overall_ready ? '🎉 System Ready!' : 'Initializing System'}
                        </h1>
                        <p className="text-white/80 text-sm font-medium" style={{ animation: 'fadeInUp 0.6s ease-out 0.3s both' }}>
                            {overall_ready ? 'Launching your workspace...' : 'Setting up your intelligent maintenance platform'}
                        </p>
                    </div>

                    {/* Progress Section */}
                    <div className="mb-8" style={{ animation: 'fadeInUp 0.6s ease-out 0.4s both' }}>
                        <div className="flex justify-between items-center text-sm text-white mb-3">
                            <span className="font-semibold drop-shadow">{current_stage}</span>
                            <span className="font-bold text-lg drop-shadow">{overall_progress}%</span>
                        </div>

                        {/* Progress Bar Container */}
                        <div className="relative h-3 bg-white/20 rounded-full overflow-hidden backdrop-blur-sm">
                            {/* Shimmer Effect */}
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />

                            {/* Progress Fill */}
                            <div
                                className={`absolute inset-y-0 left-0 rounded-full transition-all duration-700 ease-out ${overall_ready
                                    ? 'bg-gradient-to-r from-emerald-400 to-green-500'
                                    : 'bg-gradient-to-r from-blue-500 to-blue-600'
                                    }`}
                                style={{ width: `${overall_progress}%` }}
                            >
                                {/* Animated Glow */}
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent animate-slide" />
                            </div>
                        </div>

                        {!overall_ready && estimated_time_remaining > 0 && (
                            <p className="text-xs text-white/60 mt-2 text-center font-medium">
                                ⏱️ Estimated time: ~{estimated_time_remaining}s
                            </p>
                        )}
                    </div>

                    {/* Status Cards */}
                    <div className="grid grid-cols-3 gap-3 mb-6" style={{ animation: 'fadeInUp 0.6s ease-out 0.5s both' }}>
                        {/* Auth Status */}
                        <div className={`relative p-4 rounded-xl transition-all duration-500 ${status?.auth_ready
                            ? 'bg-emerald-500/20 border-2 border-emerald-400/50'
                            : 'bg-white/10 border-2 border-white/20'
                            }`}>
                            <div className="flex flex-col items-center">
                                <Shield className={`w-7 h-7 mb-2 transition-all duration-500 ${status?.auth_ready
                                    ? 'text-emerald-300 drop-shadow-glow-green animate-bounce-subtle'
                                    : 'text-white/60'
                                    }`} strokeWidth={2} />
                                <span className="text-xs font-bold text-white">Auth</span>
                                {status?.auth_ready && (
                                    <span className="text-[10px] text-emerald-300 mt-1 font-semibold animate-fade-in">✓ Ready</span>
                                )}
                            </div>
                        </div>

                        {/* Data Status */}
                        <div className={`relative p-4 rounded-xl transition-all duration-500 ${status?.data_ready
                            ? 'bg-emerald-500/20 border-2 border-emerald-400/50'
                            : 'bg-white/10 border-2 border-white/20'
                            }`}>
                            <div className="flex flex-col items-center">
                                <Database className={`w-7 h-7 mb-2 transition-all duration-500 ${status?.data_ready
                                    ? 'text-emerald-300 drop-shadow-glow-green animate-bounce-subtle'
                                    : 'text-white/60'
                                    }`} strokeWidth={2} />
                                <span className="text-xs font-bold text-white">Data</span>
                                {status?.data_ready && (
                                    <span className="text-[10px] text-emerald-300 mt-1 font-semibold animate-fade-in">✓ Ready</span>
                                )}
                            </div>
                        </div>

                        {/* Analytics Status */}
                        <div className={`relative p-4 rounded-xl transition-all duration-500 ${status?.analytics_ready
                            ? 'bg-emerald-500/20 border-2 border-emerald-400/50'
                            : 'bg-white/10 border-2 border-white/20'
                            }`}>
                            <div className="flex flex-col items-center">
                                <BarChart3 className={`w-7 h-7 mb-2 transition-all duration-500 ${status?.analytics_ready
                                    ? 'text-emerald-300 drop-shadow-glow-green animate-bounce-subtle'
                                    : 'text-white/60'
                                    }`} strokeWidth={2} />
                                <span className="text-xs font-bold text-white">Analytics</span>
                                {status?.analytics_ready && (
                                    <span className="text-[10px] text-emerald-300 mt-1 font-semibold animate-fade-in">✓ Ready</span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Footer Tip */}
                    {!overall_ready && (
                        <div className="text-center pt-4 border-t border-white/20" style={{ animation: 'fadeInUp 0.6s ease-out 0.6s both' }}>
                            <p className="text-xs text-white/70 font-medium">
                                💡 First-time setup typically takes 10-30 seconds
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Custom Animations */}
            <style jsx>{`
                @keyframes fadeInScale {
                    from {
                        opacity: 0;
                        transform: scale(0.9) translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: scale(1) translateY(0);
                    }
                }

                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(15px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                @keyframes shimmer {
                    0% { transform: translateX(-100%); }
                    100% { transform: translateX(200%); }
                }

                @keyframes slide {
                    0% { transform: translateX(-100%); }
                    100% { transform: translateX(100%); }
                }

                @keyframes spin-slow {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }

                @keyframes scale-in {
                    0% {
                        transform: scale(0);
                        opacity: 0;
                    }
                    50% {
                        transform: scale(1.2);
                    }
                    100% {
                        transform: scale(1);
                        opacity: 1;
                    }
                }

                @keyframes bounce-subtle {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-5px); }
                }

                @keyframes fade-in {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                .animate-shimmer {
                    animation: shimmer 2s infinite;
                }

                .animate-slide {
                    animation: slide 2s infinite;
                }

                .animate-spin-slow {
                    animation: spin-slow linear infinite;
                }

                .animate-scale-in {
                    animation: scale-in 0.5s ease-out;
                }

                .animate-bounce-subtle {
                    animation: bounce-subtle 2s ease-in-out infinite;
                }

                .animate-fade-in {
                    animation: fade-in 0.5s ease-out;
                }

                .drop-shadow-glow-green {
                    filter: drop-shadow(0 0 8px rgba(52, 211, 153, 0.8));
                }
            `}</style>
        </div>
    );
};

export default SystemLoadingScreen;
